import BaseApi from "./base-api";

/**
 * Class for the Aurora API
 *
 * @extends BaseApi
 * @description This class is used to interact with the Aurora endpoint
 */
class AuroraApi extends BaseApi {
  constructor() {
    super("aurora_v2");

    this.methods = {
      UPDATE_USAGE: "update_usage",
      GET_DESIGN_LINK: "get_design_link",
      GET_ADMIN_LINK: "get_admin_link",
      GET_ENFIN_APPLY_LINK: "get_enfin_apply_link",
      GET_DESIGNS_FOR_PROJECT: "get_designs_for_project",
      SWITCH_AURORA_DESIGN: "switch_aurora_design",
      SYNC_DESIGN: "sync_design",
      GET_BILL_SUMMARY: "get_bill_summary",
    };
  }

  /**
   * Update the usage and create/sync the design to aurora on the backend
   */
  updateUsage = async (usage) => {
    const response = await this.request({
      method: this.methods.UPDATE_USAGE,
      ...usage,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not update usage in Aurora");
  };

  /**
   * Get the admin link from the email address provided
   */
  getAdminLink = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_ADMIN_LINK,
      sf_project_id: projectId,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get admin link from Aurora");
  };

  /**
   * Get the EnFin apply link for a Salesforce project.
   */
  getEnfinApplyLink = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_ENFIN_APPLY_LINK,
      sf_project_id: projectId,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get EnFin apply link from Aurora");
  };

  /**
   * Get the design link from the email address provided
   */
  getDesignLink = async ({ email, projectId }) => {
    const response = await this.request({
      method: this.methods.GET_DESIGN_LINK,
      rep_email: email,
      sf_project_id: projectId,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get design link from Aurora");
  };

   /**
   * Get aurora designs for the project/saleforce id
   */
  getDesignsForProject = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_DESIGNS_FOR_PROJECT,
      sf_project_id: projectId,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get project designs from Aurora");
  };
  
   /**
   * Switch the design to aurora.
   */
  switchAuroraDesign = async ({ projectId, auroraDesignId }) => {
    const response = await this.request({
      method: this.methods.SWITCH_AURORA_DESIGN,
      sf_project_id: projectId,
      aurora_design_id: auroraDesignId
    });

    if (response) {
      return response;
    }

    throw new Error("Could not switch design to Aurora");
  };

  /**
   * Sync the design to aurora.
   * - This will pull a few different pieces of data that is then used to update the proposal version.
   * @param {string} projectId - Salesforce project ID
   * @param {string} designId - Optional Aurora design ID to sync
   * @param {boolean} should_sync - Whether to actually sync the design (default: true). Set to false to only get information.
   */
  syncDesign = async ({ projectId, designId = null, should_sync = true, propId = null }) => {
    const requestData = {
      method: this.methods.SYNC_DESIGN,
      sf_project_id: projectId,
      should_sync: should_sync,
    };
    
    if (designId) {
      requestData.aurora_design_id = designId;
    }
    
    if (propId) {
      requestData.prop_id = propId;
    }

    const response = await this.request(requestData);

    if (response) {
      return response;
    }

    throw new Error("Could not sync design in Aurora");
  };

  getBillSummary = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_BILL_SUMMARY,
      sf_project_id: projectId,
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get bill summary from Aurora");
  };
}

export default new AuroraApi();
